// 261015 P5a (D5) — diễn giải dấu tự áp `diff.autoApply` của BE thành chữ người đọc: dòng trên thẻ khối, toast, hộp xác nhận khôi phục.
// Hàm thuần, KHÔNG import antd/react (jest). `assistantView.ts` re-export các hàm này (doc chỉ định `autoApplyNote` ở đó).
import type { AssistantBlock, AssistantUiStage, AutoApplyMark } from '../../../../types/adaptationAssistant';

const str = (v: unknown): string => String(v ?? '').trim();

export const AUTO_APPLIED_TEXT = 'Đã tự áp sau khi kiểm (0 lỗi mới)';
export const SKIPPED_PREFIX = 'Chưa tự áp: ';
/** Câu chung cho reason lạ / thiếu — luôn dẫn về hành động của người. */
export const AUTO_SKIP_GENERIC = 'cần bạn xem rồi bấm Áp';
/** Khôi phục bản ② luôn đưa khung xương về nháp (BE `undoApply`) — nói thẳng khi bản đó do máy tự áp. */
export const AUTO_UNDO_DRAFT_HINT = 'Khôi phục sẽ đưa khung xương về nháp, phải chốt lại.';

const UNSIGN_TEXT: Record<string, string> = {
  outlineApproved: 'khung xương đã chốt — bản sửa này sẽ làm mất dấu chốt, bạn bấm Áp nếu đồng ý',
  overviewEffective: 'tổng quan truyện đã chốt — bản sửa này sẽ làm tổng quan hết hiệu lực, bạn bấm Áp nếu đồng ý',
  episodePlanApproved: 'dàn tập đã chốt — bản sửa này sẽ làm mất dấu chốt, bạn bấm Áp nếu đồng ý',
};
const UNSIGN_GENERIC = 'bản sửa này sẽ làm mất một dấu chốt, bạn bấm Áp nếu đồng ý';

/** Mã `reason` BE (gate `assistantAutoApply.ts` + mã lỗi `adapter.apply`) → câu. Khoá literal ở test — thêm reason mới ở BE phải thêm ở đây. */
const REASON_TEXT: Record<string, string> = {
  MANY_BLOCKS: 'AI đề xuất nhiều khối cùng lúc — xem từng khối rồi bấm Áp',
  NOT_FIX: 'lượt này không phải "Nhờ AI sửa" một bấm',
  JOB_PREVIEW: 'khối này cần xem thử bằng AI (tốn thêm) nên do bạn bấm',
  REJECTED_OPS: 'có lệnh bị loại khi xem thử — xem lại rồi bấm Áp',
  NEW_ERROR: 'bản sửa làm phát sinh lỗi mới',
  STAGE_NOT_IN_SCOPE: 'bước này không tự áp',
  HUMAN_ONLY_OP: 'có lệnh chỉ người mới được quyết (chấp nhận cảnh báo, sửa tổng quan, chấp nhận gieo…)',
  TOUCHES_WRITTEN: 'bản sửa ảnh hưởng tới tập đã viết — bạn xem rồi quyết',
  EPISODES_STALE: 'bản sửa làm danh sách tập cần chia lại ở ④',
  EXIT_CHANGED: 'đoạn kết tập đổi (kéo theo kiểm nối tập sau) — bạn xem rồi bấm Áp',
  OP_NOT_ALLOWED: 'lệnh này cần người tự áp',
  PREVIEW_STALE: 'xem thử đã cũ (dữ liệu vừa đổi) — bấm "Xem thử lại"',
  PREVIEW_NOT_READY: 'xem thử chưa xong lúc áp',
  STATE_CONFLICT: 'khối vừa được xử lý ở nơi khác',
  OUTLINE_CHANGED: 'khung xương / chia tập vừa bị thay đổi bởi thao tác khác — xem thử lại',
  SCRIPT_CHANGED: 'kịch bản vừa bị thay đổi bởi thao tác khác — xem thử lại',
  CONTENT_DIVERGED: 'nội dung đã khác lúc đề xuất — xem thử lại',
  NOTHING_APPLIED: 'không lệnh nào áp được trên bản hiện tại',
  HAS_ERRORS: 'bản sửa còn lỗi chặn',
  JOB_RUNNING: 'đang có tác vụ khác chạy cho truyện này',
  THREAD_BUSY: 'luồng đang bận một lượt khác',
  ALREADY_APPLIED: 'khối đã được áp trước đó',
  OVERVIEW_NOT_EFFECTIVE: 'tổng quan truyện chưa chốt hoặc đã cũ — mở Tổng quan truyện ở ② để kiểm',
  OVERVIEW_CHANGED: 'tổng quan truyện vừa đổi — mở Tổng quan truyện ở ② để kiểm',
  APPLY_FAILED: 'lúc áp có lỗi — bạn thử bấm Áp lại',
  // 261023 3g Q4: lượt «Nhờ AI sửa» một dòng lỗi — chỉ tự áp khi lỗi đó đã hết và chỉ có lệnh đúng loại.
  TARGET_NOT_RESOLVED: 'lỗi bạn nhờ sửa vẫn còn sau bản sửa này — xem rồi bấm Áp nếu đồng ý',
  OP_NOT_ALLOWED_FOR_TARGET: 'bản sửa có lệnh ngoài việc gỡ lỗi bạn nhờ (vd bỏ/gộp nhân vật) — xem kỹ rồi bấm Áp',
  TARGET_NOT_AUTO_FIXABLE: 'lỗi này máy chưa tự áp được — xem trước rồi bấm Áp',
};

/** Danh sách reason BE mà FE biết (khoá literal ở test): cổng D4 → 3 chỗ chốt → EXIT/OP → mã lỗi áp. */
export const AUTO_APPLY_REASONS_FROM_BE: readonly string[] = [
  'MANY_BLOCKS', 'NOT_FIX', 'JOB_PREVIEW', 'REJECTED_OPS', 'NEW_ERROR', 'STAGE_NOT_IN_SCOPE', 'HUMAN_ONLY_OP', 'TOUCHES_WRITTEN', 'EPISODES_STALE',
  'WOULD_UNSIGN:outlineApproved', 'WOULD_UNSIGN:overviewEffective', 'WOULD_UNSIGN:episodePlanApproved', 'EXIT_CHANGED', 'OP_NOT_ALLOWED',
  'PREVIEW_STALE', 'PREVIEW_NOT_READY', 'STATE_CONFLICT', 'OUTLINE_CHANGED', 'SCRIPT_CHANGED', 'CONTENT_DIVERGED', 'NOTHING_APPLIED', 'HAS_ERRORS',
  'JOB_RUNNING', 'THREAD_BUSY', 'ALREADY_APPLIED', 'OVERVIEW_NOT_EFFECTIVE', 'OVERVIEW_CHANGED', 'APPLY_FAILED',
  'TARGET_NOT_RESOLVED', 'OP_NOT_ALLOWED_FOR_TARGET', 'TARGET_NOT_AUTO_FIXABLE',   // 261023 3g Q4
];

/** Câu cho một `reason` (không gồm tiền tố "Chưa tự áp: "). Reason lạ / thiếu ⇒ câu chung — không vỡ khi BE thêm mã mới. */
export const autoApplyReasonText = (reason: string | undefined | null): string => {
  const r = str(reason);
  if (r.startsWith('WOULD_UNSIGN:')) return UNSIGN_TEXT[r.slice('WOULD_UNSIGN:'.length)] ?? UNSIGN_GENERIC;
  return REASON_TEXT[r] ?? AUTO_SKIP_GENERIC;
};

/** `block.diff.autoApply` đọc thẳng từ diff gốc (các hàm `…DiffOf` dựng lại diff và làm rơi trường lạ). */
export const autoApplyMarkOf = (block: Pick<AssistantBlock, 'diff'>): AutoApplyMark | null => {
  const m = (block.diff as { autoApply?: unknown } | null | undefined)?.autoApply as Partial<AutoApplyMark> | undefined;
  if (!m || typeof m !== 'object') return null;
  if (m.decision !== 'APPLIED' && m.decision !== 'SKIPPED') return null;
  return { decision: m.decision, ...(m.reason !== undefined ? { reason: str(m.reason) } : {}), ...(m.at !== undefined ? { at: str(m.at) } : {}) };
};

export interface AutoApplyNote { kind: 'applied' | 'skipped'; text: string }

/** Khối chưa có kết cục (còn chờ người) — chỗ "Chưa tự áp" hiện. APPLIED/RESTORED/DISMISSED thì dòng này không còn đúng. */
const OPEN_STATES = ['PROPOSED', 'PREVIEW_RUNNING', 'READY', 'STALE', 'FAILED'];

/**
 * Dòng ghi chú trên thẻ khối:
 *  - APPLIED + `decision==='APPLIED'` + chưa từng Khôi phục (`appliedResult.restoredAt` sống qua "Áp lại" ⇒ lần áp sau là của người) ⇒ "Đã tự áp…".
 *  - `SKIPPED` ở khối còn chờ người (kể cả `MANY_BLOCKS`/`JOB_PREVIEW` — khối không ở READY) ⇒ "Chưa tự áp: <câu theo reason>".
 */
export const autoApplyNote = (block: Pick<AssistantBlock, 'diff' | 'state' | 'appliedResult'>): AutoApplyNote | null => {
  const m = autoApplyMarkOf(block);
  if (!m) return null;
  if (m.decision === 'APPLIED') {
    return block.state === 'APPLIED' && !block.appliedResult?.restoredAt ? { kind: 'applied', text: AUTO_APPLIED_TEXT } : null;
  }
  return OPEN_STATES.includes(block.state) ? { kind: 'skipped', text: `${SKIPPED_PREFIX}${autoApplyReasonText(m.reason)}` } : null;
};

/** Toast 8 giây sau lượt `fix` có khối vừa tự áp. null = không phải khối tự áp. */
export const autoApplyToastText = (block: Pick<AssistantBlock, 'diff' | 'state' | 'appliedResult' | 'title'>): string | null =>
  autoApplyNote(block)?.kind === 'applied' ? `Đã tự áp “${str(block.title)}” sau khi kiểm (0 lỗi mới).` : null;

/** Hộp xác nhận "Khôi phục bản trước" — dùng ở toast (thẻ có Popconfirm riêng). ② nói thẳng cái sẽ mất. */
export const undoConfirmText = (stageKey: AssistantUiStage): { title: string; content: string } =>
  stageKey === 'skeleton'
    ? { title: 'Khôi phục bản trước?', content: `Khung xương về bản trước khi áp. ${AUTO_UNDO_DRAFT_HINT} Bản hiện tại vẫn còn ở mục Phiên bản.` }
    : { title: 'Khôi phục bản trước?', content: 'Quay về bản trước khi áp đề xuất này. Bản hiện tại vẫn còn ở mục Phiên bản.' };
